import {
  seedArtifactIsNew,
  type ChatSeedBuildInput,
  type ChatSeedPreSubmitContext,
  type ChatSeedPreSubmitResult,
  type ChatSeedSubmitParams,
} from '../../chat-seed/types';
import { getDatasetSeedRuntime } from './runtime';

const DATASET_ARTIFACT_TYPE = 'dataset';

function resolveDatasetSeedInputFromSubmit(
  params: ChatSeedSubmitParams
): ChatSeedBuildInput | null {
  const artifact = params.initialArtifacts?.find(
    (entry) => entry.artifactType === DATASET_ARTIFACT_TYPE
  );
  if (artifact === undefined || artifact.artifactId === undefined) {
    return null;
  }

  const data = artifact.data as { isNew?: boolean } | null | undefined;

  return {
    artifactId: artifact.artifactId,
    isNew: data?.isNew === true,
  };
}

async function createDatasetOnServer(
  ctx: ChatSeedPreSubmitContext,
  input: ChatSeedBuildInput
): Promise<ChatSeedPreSubmitResult> {
  const createResult = await ctx.clients.dataset.createManual({
    id: input.artifactId,
    ...(ctx.folderId !== undefined && { folderId: ctx.folderId }),
  });

  if (createResult.isErr()) {
    const message =
      createResult.error.t === 'application'
        ? createResult.error.err.message ?? 'Failed to create dataset'
        : 'Failed to create dataset';
    return { ok: false, message };
  }

  return { ok: true };
}

/**
 * Creates the seeded dataset before the first message goes out, so the dataset
 * tools the editing skill loads have something to read and write. Prefer the
 * page's runtime (it flips the model to persisted in place, so the panel drops
 * the empty-state without a refetch); fall back to a direct create when no page
 * model is mounted for this id.
 */
async function persistNewDataset(
  ctx: ChatSeedPreSubmitContext,
  input: ChatSeedBuildInput
): Promise<ChatSeedPreSubmitResult> {
  const runtime = getDatasetSeedRuntime(input.artifactId);
  if (runtime === undefined) {
    return createDatasetOnServer(ctx, input);
  }
  if (runtime.isPersisted()) {
    return { ok: true };
  }
  return runtime.persist({
    ...(ctx.folderId !== undefined && { folderId: ctx.folderId }),
  });
}

export async function datasetChatSeedPreSubmit(
  ctx: ChatSeedPreSubmitContext
): Promise<ChatSeedPreSubmitResult | null> {
  const input = resolveDatasetSeedInputFromSubmit(ctx.params);
  if (
    input === null ||
    !seedArtifactIsNew(ctx.params, DATASET_ARTIFACT_TYPE, input.artifactId)
  ) {
    return null;
  }

  return persistNewDataset(ctx, input);
}
